import {
    Alert, Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid, MenuItem,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import cookie from "react-cookies";
import {Permission} from "../../config/permission";

interface PlayerBuildingsState {
    loaded: boolean;
    environment: any;
    showModal: boolean;
    newBuilding: {
        id: string;
        level: string;
        position: string;
    }
}

interface PlayerBuildingsProp {
    playerId: string;
    activeEnv: any;
    playerData: any;
    heroesConfig: any;
    form: any;
}

class PlayerBuildingsMain extends React.Component<PlayerBuildingsProp, PlayerBuildingsState> {
    isSuperAdmin: boolean = false;
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            environment: [],
            showModal: false,
            newBuilding: {
                id: "0",
                level: "0",
                position: "0",
            }
        };
    }

    componentDidMount() {
        this.fetchData();
        const permissions = cookie.load("permissions");
        this.isSuperAdmin = permissions && permissions === Permission.SUPER_ADMIN; //superadmin, hardcode for now
    }

    async fetchData() {
        try {
            const response = await axios.get(
                `${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/buildings`,
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    },
                },
            );
            this.setState({
                environment: response.data,
                loaded: true,
            });
        } catch (e) {
            PopupMessage.error("Failed to load buildings: " + e.message);
        }
    }

    getBuildingName(building) {
        const item = getGlobalVar("BUILDINGS").find(el => el.id === building.id);
        return item ? item.name : "-";
    }

    async removeBuilding(index: number) {
        if ("prod" !== this.props.activeEnv && confirm("Do you really want to delete this item?")) {
            try {
                const response = await axios.delete(
                    `${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/buildings/${index}`,
                    {
                        headers: {
                            Authorization: Helpers.getSessionId(),
                            env: this.props.activeEnv,
                        },
                    },
                );
                const environment = {...this.state.environment}
                environment.buildings = response.data;
                this.setState({
                    environment,
                });
                PopupMessage.success("Building removed!");
            } catch (e) {
                PopupMessage.error("Building delete failed: " + e.message);
            }
        }
    }

    async changeBuildingLevel(index: number, id: number, level: number) {
        try {
            const response = await axios.put(
                `${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/buildings`,
                {
                    index,
                    id,
                    level,
                },
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    },
                },
            );
            const environment = {...this.state.environment}
            environment.buildings = response.data;
            this.setState({
                environment,
            });
            PopupMessage.success("Building level changed!");
        } catch (e) {
            PopupMessage.error("Failed to update building level: " + e.message);
        }
    }

    onChangeNewBuildingData = (e) => {
        const data = {...this.state.newBuilding}
        data[e.target.name.replace("building-", "")] = e.target.value
        this.setState({
            newBuilding: data,
        });
    }

    onChangeBuildingLevel = (e) => {
        const data = e.target.name.split("|");
        const index = data[0];
        const id = data[1];
        const level = e.target.value

        this.changeBuildingLevel(parseInt(index, 10), parseInt(id, 10), parseInt(level, 10));
    }

    addBuilding = async () => {
        try {
            const response = await axios.post(
                `${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/buildings`,
                {
                    id: +this.state.newBuilding.id,
                    level: +this.state.newBuilding.level,
                    position: +this.state.newBuilding.position,
                },
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    },
                },
            );
            const environment = {...this.state.environment}
            environment.buildings = response.data;
            this.setState({
                environment,
                showModal: false,
            });
            PopupMessage.success("Building added!");
        } catch (e) {
            PopupMessage.error("Failed to add building: " + e.message);
        }
    }

    openAddBuildingModal = () => {
        if ("prod" !== this.props.activeEnv) {
            this.setState({
                showModal: true,
            })
        }
    }

    cancelAddBuilding = () => {
        this.setState({
            showModal: false,
        })
    }

    renderBuildingIdDropdown() {
        return getGlobalVar("BUILDINGS").map(building => <MenuItem value={building.id} key={building.id}>
            {Helpers.getLocalizationName(building.name)}
        </MenuItem>);
    }

    renderBuildingLevelDropdown = (item) => {
        const building = getGlobalVar("BUILDINGS").find(el => el.id === item.id);
        const items = [];
        if (building && building.levels) {
            for (let level = 1; level <= building.levels.length; level++) {
                items.push(<MenuItem value={"" + level} key={level}>{level}</MenuItem>);
            }
        }
        return items;
    }

    displayBuildings() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={8}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.environment.buildings.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={8}>
                    <Alert severity="info">No Buildings</Alert>
                </TableCell>
            </TableRow>
        }

        return this.state.environment.buildings.map(item => {
            let element = <div>{item.level}</div>;
            if ("prod" !== this.props.activeEnv) {
                element = (
                    <Select
                        name={item.index + "|" + item.id}
                        value={item.level}
                        size="small"
                        onChange={this.onChangeBuildingLevel}
                    >
                        {this.renderBuildingLevelDropdown(item)}
                    </Select>
                );
            }
            return (
                <TableRow key={item.objectId}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.index}</TableCell>
                    <TableCell>{Helpers.getLocalizationName(this.getBuildingName(item))}</TableCell>
                    <TableCell>
                        {element}
                    </TableCell>
                    <TableCell>{item.position}</TableCell>
                    <TableCell>{item.isRepaired ? "Yes" : "No"}</TableCell>
                    <TableCell>{item.isProcessing}</TableCell>
                    <TableCell>
                        {
                            this.isSuperAdmin || (item.id !== 5000 && item.id !== 5001) ? 
                            <Button
                                disableElevation
                                variant="contained"
                                color="secondary"
                                onClick={() => this.removeBuilding(item.index)}
                                size="small"
                            >
                                Delete
                            </Button> : null
                        }
                    </TableCell>
                </TableRow>
            )
            }
        );
    }

    displayObstacles() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={2}>
                    {Helpers.renderSkeletonRows(2, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.environment.obstacles.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={2}>
                    <Alert severity="info">No Obstacles</Alert>
                </TableCell>
            </TableRow>
        }

        return this.state.environment.obstacles.map(item =>
            (
                <TableRow key={item.objectId}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.level}</TableCell>
                </TableRow>
            )
        );
    }

    displayDecorations() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={3}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.environment.decorations.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No Decorations</Alert>
                </TableCell>
            </TableRow>
        }

        return this.state.environment.decorations.map(item =>
            (
                <TableRow key={item.objectId}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{Helpers.getLocalizationName(this.getBuildingName(item))}</TableCell>
                    <TableCell>{item.level}</TableCell>
                </TableRow>
            )
        );
    }

    render() {
        const style = {
            overflowX: "auto",
        } as React.CSSProperties;
        return <Grid container spacing={3}>
            <Grid item xs={6}>
                <Card elevation={0} style={style}>
                    <CardHeader
                        title="Buildings"
                        action={
                            this.state.loaded ? <Button
                                disableElevation
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={this.openAddBuildingModal}
                                style={{ marginTop: 5, marginRight: 5 }}
                            >
                                Add Building
                            </Button> : <Skeleton
                                width={110}
                                height={45}
                                style={{ marginRight: 5 }}
                            />
                        }
                    />
                    <CardContent>
                       <Table>
                           <TableHead>
                               <TableRow>
                                   <TableCell>ID</TableCell>
                                   <TableCell>Index</TableCell>
                                   <TableCell>Name</TableCell>
                                   <TableCell>Level</TableCell>
                                   <TableCell>Position</TableCell>
                                   <TableCell>Repaired</TableCell>
                                   <TableCell>Processing</TableCell>
                                   {this.isSuperAdmin ? <TableCell>Actions</TableCell> : null}
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {this.displayBuildings()}
                           </TableBody>
                       </Table>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={3}>
                <Card elevation={0}>
                    <CardHeader title="Decorations"/>
                    <CardContent>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Level</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {this.displayDecorations()}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={3}>
                <Card elevation={0}>
                    <CardHeader title="Obstacles"/>
                    <CardContent>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Level</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {this.displayObstacles()}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Grid>
            <Dialog
                open={this.state.showModal}
                fullWidth={true}
                maxWidth="sm"
            >
                <DialogTitle>
                    Add new building
                </DialogTitle>

                <DialogContent dividers>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>Building ID</TableCell>
                                <TableCell>
                                    <Select
                                        name="building-id"
                                        onChange={this.onChangeNewBuildingData}
                                        size="small"
                                        style={{ width: "100%" }}
                                    >
                                        <MenuItem value="0" disabled key="0">Select building</MenuItem>
                                        {this.renderBuildingIdDropdown()}
                                    </Select>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Building level</TableCell>
                                <TableCell>
                                    <Select
                                        name="building-level"
                                        onChange={this.onChangeNewBuildingData}
                                        size="small"
                                        style={{ width: "100%" }}
                                    >
                                        <MenuItem value="0" disabled key="0">Select level</MenuItem>
                                        {this.renderBuildingLevelDropdown({id: +this.state.newBuilding.id})}
                                    </Select>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Position</TableCell>
                                <TableCell>
                                    <TextField
                                        name="building-position"
                                        onChange={this.onChangeNewBuildingData}
                                        size="small"
                                        style={{ width: "100%" }}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </DialogContent>

                <DialogActions>
                    <Button
                        disableElevation
                        onClick={this.cancelAddBuilding}
                        variant="contained"
                        color="secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disableElevation
                        onClick={this.addBuilding}
                        variant="contained"
                        color="primary"
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>;
    }
}

export const PlayerBuildings = createForm()(PlayerBuildingsMain);
